import { AlbumStory } from '@src/types.ts/story';
import { getAiDescriptionResponse } from '@src/utility/story';
import { NewStoryUpdate } from '../NewStoriesQuerySubscriptionComponent';

export const buildTargetUrl = (
  params: URLSearchParams,
  storyId: string,
  playbackId: string | undefined,
) =>
  `?storyId=${storyId}&env=${params.get('env')}&playbackId=${playbackId || ''}`;

export const hasUploadFailed = (s: NewStoryUpdate): boolean =>
  s.useAws
    ? !!s.uploadLog?.handleFailure
    : !!s.uploadLog?.handleFailure &&
      s.uploadLog?.initialUpload?.status !== 'success';

export const isVideoUploadStuck = (s: NewStoryUpdate): boolean =>
  s.uploadLog?.initialUpload?.status !== 'success' &&
  Date.now() - new Date(s.uploadLog?.initialUpload?.updatedAt).getTime() >
    1500000;

export const isStoryOriginalVideoReady = (s: NewStoryUpdate): boolean =>
  !s.useAws
    ? !!s.originalVideo?.video?.thumbnailUrl
    : s.uploadLog?.ffmpegProcessing?.status === 'success';

export const isStoryTranscriptionReady = (s: NewStoryUpdate): boolean =>
  s.useAws
    ? s.uploadLog?.combineResults?.status === 'success'
    : ['FAILED', 'COMPLETED'].includes(s.transcription?.jobStatus);

export const isStoryCompressedVideoReady = (s: NewStoryUpdate): boolean =>
  s.useAws
    ? s.uploadLog?.extractThumbnailsFromLowRes?.status === 'success' ||
      s.uploadLog?.compressVideo?.output?.jobs?.some(
        (j: any) =>
          j &&
          j.resolution === '270' &&
          (j.status || '').toLowerCase() === 'complete',
      )
    : !!s.originalVideo?.video;

export const isStoryProcessingStarted = (s: NewStoryUpdate): boolean =>
  s.uploadLog?.initialUpload?.status === 'success';

export const calculateCompletionPercent = (story: AlbumStory): number => {
  if (
    isStoryOriginalVideoReady(story) &&
    isStoryTranscriptionReady(story) &&
    isStoryCompressedVideoReady(story)
  ) {
    return 100;
  }
  if (isStoryOriginalVideoReady(story)) {
    return 75;
  }
  if (isStoryProcessingStarted(story)) {
    return 50;
  }
  return Math.round((story.uploadLog?.initialUpload?.progress || 0) / 2);
};

export const getDescription = (story: AlbumStory) =>
  getAiDescriptionResponse(story)?.response?.toString() || '';

export const isStoryLoading = (s: AlbumStory): boolean =>
  s.externalUploadStatus !== 'FAILED' &&
  !hasUploadFailed(s) &&
  (!isStoryOriginalVideoReady(s) ||
    !isStoryTranscriptionReady(s) ||
    !isStoryCompressedVideoReady(s));

export const isStoryAbandoned = (s: AlbumStory): boolean => {
  if (s.externalUploadStatus === 'CANCELLED') return true;
  if (
    s.externalUploadStatus === 'COMPLETED' ||
    s.externalUploadStatus === 'FAILED'
  ) {
    return false;
  }
  if (s.uploadLog?.initialUpload?.status !== 'success') {
    const lastUploadActivity = s.uploadLog?.initialUpload?.updatedAt;
    if (!lastUploadActivity) return false; // No activity data, assume not abandoned
    const timeSinceLastActivity =
      Date.now() - new Date(lastUploadActivity).getTime();
    const UPLOAD_STUCK_THRESHOLD = 10 * 60 * 1000; // 10 minutes
    return timeSinceLastActivity > UPLOAD_STUCK_THRESHOLD;
  }
  return false;
};
